import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Camera, UploadSimple, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money, FILE_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const METHODS = [
  ["actual_markup", "Actual cost + markup"],
  ["retail_markup", "Retail + markup"],
  ["manual", "Manual price"],
  ["customer_supplied", "Customer supplied"],
  ["not_billable", "Not billable"],
];

const Field = ({ label, ...p }) => (
  <label className="block">
    <span className="text-[10px] uppercase font-bold text-zinc-400">{label}</span>
    <Input {...p} className="h-10 mt-0.5" />
  </label>
);

export default function ReceiptScan() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const presetJob = sp.get("job") || "";
  const editId = sp.get("id") || "";
  const [rec, setRec] = useState(null);
  const [dup, setDup] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobMats, setJobMats] = useState({});
  const [busy, setBusy] = useState(false);
  const camRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/jobs?limit=40").then((r) => setJobs(r.data)).catch((e) => console.error("load jobs failed:", e));
    if (editId) api.get(`/receipts/${editId}`).then((r) => setRec(r.data)).catch(() => toast.error("Receipt not found"));
  }, [editId]);

  const ensureMats = async (jid) => {
    if (!jid || jobMats[jid]) return;
    try { const r = await api.get(`/jobs/${jid}`); setJobMats((m) => ({ ...m, [jid]: r.data.materials || [] })); } catch (e) { console.error("load job materials failed:", e); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const url = presetJob ? `/receipts/scan?job_id=${presetJob}` : "/receipts/scan";
      const r = await api.post(url, fd);
      setRec(r.data.receipt);
      setDup(r.data.duplicate);
      if (r.data.duplicate) toast.warning("Possible duplicate receipt detected");
      else toast.success("Receipt scanned — review the details");
      (r.data.receipt.items || []).forEach((it) => it.job_id && ensureMats(it.job_id));
    } catch (er) {
      toast.error("Couldn't read that file. Try a clearer photo.");
    } finally { setBusy(false); e.target.value = ""; }
  };

  const setField = (patch) => setRec((x) => ({ ...x, ...patch }));
  const setItem = (i, patch) => setRec((x) => {
    const items = [...x.items];
    items[i] = { ...items[i], ...patch };
    return { ...x, items };
  });

  const save = async () => {
    setBusy(true);
    try {
      const saved = await api.put(`/receipts/${rec.id}`, rec);
      toast.success("Receipt saved & costs applied");
      if (presetJob) nav(`/job/${presetJob}`);
      else nav("/receipts");
    } catch (er) { toast.error(er.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const num = (v) => (v === "" || v == null ? 0 : Number(v));

  // ---------- capture screen ----------
  if (!rec) {
    return (
      <div className="min-h-screen">
        <header className="sticky top-0 z-30 bg-zinc-50/95 backdrop-blur border-b border-zinc-200 px-4 py-3 flex items-center gap-3">
          <button data-testid="receipt-back-btn" onClick={() => nav(-1)} className="text-zinc-500"><ArrowLeft size={24} /></button>
          <h1 className="font-heading text-xl font-black tracking-tight">Scan Receipt</h1>
        </header>
        <div className="p-6 flex flex-col items-center text-center gap-6 mt-10">
          <div className="w-24 h-24 rounded-3xl bg-orange-50 text-orange-600 flex items-center justify-center"><Camera size={52} weight="fill" /></div>
          <p className="text-zinc-500 text-sm max-w-xs">Snap a photo of a supplier receipt, or upload an image / PDF. We’ll read the items, prices and tax.</p>
          <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={onFile} data-testid="receipt-camera-input" />
          <input ref={fileRef} type="file" accept="image/*,application/pdf" hidden onChange={onFile} data-testid="receipt-file-input" />
          <div className="w-full max-w-xs space-y-3">
            <Button data-testid="take-photo-btn" disabled={busy} onClick={() => camRef.current?.click()} className="w-full h-14 text-base font-bold bg-orange-600 hover:bg-orange-700 gap-2">
              <Camera size={22} weight="fill" /> {busy ? "Reading…" : "Take Photo"}
            </Button>
            <Button data-testid="upload-receipt-btn" disabled={busy} variant="outline" onClick={() => fileRef.current?.click()} className="w-full h-14 text-base font-bold gap-2">
              <UploadSimple size={22} weight="bold" /> Upload Image / PDF
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- review screen ----------
  return (
    <div className="min-h-screen pb-28">
      <header className="sticky top-0 z-30 bg-zinc-50/95 backdrop-blur border-b border-zinc-200 px-4 py-3 flex items-center gap-3">
        <button data-testid="receipt-back-btn" onClick={() => nav(-1)} className="text-zinc-500"><ArrowLeft size={24} /></button>
        <h1 className="font-heading text-xl font-black tracking-tight">Review Receipt</h1>
      </header>

      <div className="p-4 space-y-5">
        {dup && (
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-3.5" data-testid="duplicate-warning">
            <div className="flex items-center gap-2 text-amber-700 font-bold"><WarningCircle size={20} weight="fill" /> POSSIBLE DUPLICATE RECEIPT</div>
            <p className="text-sm text-amber-800 mt-1">A saved receipt from {dup.store} on {dup.purchase_date} for {money(dup.total)} already exists. Continue only if this is a different purchase.</p>
          </div>
        )}

        {rec.file_url && (
          <img src={FILE_URL(rec.file_url)} alt="receipt" data-testid="receipt-image" className="w-full max-h-72 object-contain rounded-xl border border-zinc-200 bg-white" />
        )}

        <section className="grid grid-cols-2 gap-3">
          <Field label="Store" value={rec.store || ""} onChange={(e) => setField({ store: e.target.value })} data-testid="receipt-store" />
          <Field label="Location" value={rec.store_location || ""} onChange={(e) => setField({ store_location: e.target.value })} data-testid="receipt-location" />
          <Field label="Date" type="date" value={rec.purchase_date || ""} onChange={(e) => setField({ purchase_date: e.target.value })} data-testid="receipt-date" />
          <Field label="Receipt #" value={rec.receipt_number || ""} onChange={(e) => setField({ receipt_number: e.target.value })} data-testid="receipt-number" />
          <Field label="Subtotal" type="number" value={rec.subtotal ?? 0} onChange={(e) => setField({ subtotal: num(e.target.value) })} data-testid="receipt-subtotal" />
          <Field label="Purchase Tax" type="number" value={rec.tax ?? 0} onChange={(e) => setField({ tax: num(e.target.value) })} data-testid="receipt-tax" />
          <Field label="Total" type="number" value={rec.total ?? 0} onChange={(e) => setField({ total: num(e.target.value) })} data-testid="receipt-total" />
          <Field label="Payment" value={rec.payment_type || ""} onChange={(e) => setField({ payment_type: e.target.value })} data-testid="receipt-payment" />
        </section>

        <section className="space-y-3">
          <h2 className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400">Items — assign each line</h2>
          {(rec.items || []).map((it, i) => (
            <ItemCard key={it.id} it={it} i={i} jobs={jobs} jobMats={jobMats} ensureMats={ensureMats}
              setItem={setItem} presetJob={presetJob} />
          ))}
          {(rec.items || []).length === 0 && <p className="text-sm text-zinc-400">No line items were read. Try a clearer photo.</p>}
        </section>
      </div>

      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white border-t border-zinc-200 p-4 flex items-center gap-3 z-40">
        <div><p className="text-[10px] uppercase font-bold text-zinc-400">Receipt Total</p>
          <p className="font-heading font-black text-xl text-zinc-900">{money(rec.total)}</p></div>
        <Button data-testid="save-receipt-btn" onClick={save} disabled={busy} className="ml-auto h-12 px-6 text-base font-bold bg-orange-600 hover:bg-orange-700">
          {busy ? "Saving…" : "Save Receipt"}
        </Button>
      </div>
    </div>
  );
}

const ItemCard = ({ it, i, jobs, jobMats, ensureMats, setItem, presetJob }) => {
  const mats = (it.job_id && jobMats[it.job_id]) || [];
  const suggestion = mats.find((m) => {
    const a = (m.name || "").toLowerCase(), b = (it.name || "").toLowerCase();
    if (!a || !b) return false;
    const tok = b.split(/\s+/).filter((w) => w.length > 3);
    return tok.some((w) => a.includes(w)) && !m.receipt_id;
  });
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-3.5 space-y-2.5" data-testid={`receipt-item-${i}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <Input value={it.name || ""} onChange={(e) => setItem(i, { name: e.target.value })} className="h-9 font-semibold" data-testid={`item-name-${i}`} />
          <p className="text-[11px] text-zinc-400 mt-1 truncate">Receipt text: “{it.raw_text}”</p>
        </div>
        {it.uncertain && <span className="text-[10px] font-bold uppercase bg-amber-100 text-amber-700 px-2 py-1 rounded-full shrink-0" data-testid={`item-uncertain-${i}`}>Check ?</span>}
      </div>

      <div className="grid grid-cols-3 gap-2">
        <label className="block"><span className="text-[10px] uppercase font-bold text-zinc-400">Qty</span>
          <Input type="number" value={it.quantity ?? 1} onChange={(e) => setItem(i, { quantity: Number(e.target.value) })} className="h-9 mt-0.5" data-testid={`item-qty-${i}`} /></label>
        <label className="block"><span className="text-[10px] uppercase font-bold text-zinc-400">Unit cost</span>
          <Input type="number" value={it.unit_price ?? 0} onChange={(e) => setItem(i, { unit_price: Number(e.target.value), line_total: Number(e.target.value) * Number(it.quantity || 1) })} className="h-9 mt-0.5" data-testid={`item-unit-${i}`} /></label>
        <label className="block"><span className="text-[10px] uppercase font-bold text-zinc-400">Line total</span>
          <Input type="number" value={it.line_total ?? 0} onChange={(e) => setItem(i, { line_total: Number(e.target.value) })} className="h-9 mt-0.5" data-testid={`item-total-${i}`} /></label>
      </div>

      {/* Assignment */}
      <div className="flex gap-1.5">
        {[["job", "Job"], ["business", "Stock"], ["expense", "Expense"], ["exclude", "Exclude"]].map(([v, l]) => (
          <button key={v} data-testid={`item-assign-${v}-${i}`}
            onClick={() => setItem(i, { assignment_type: v })}
            className={`flex-1 h-9 rounded-lg text-xs font-bold border transition-colors ${it.assignment_type === v ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}>{l}</button>
        ))}
      </div>

      {it.assignment_type === "job" && (
        <div className="space-y-2 border-t border-zinc-100 pt-2.5">
          <select data-testid={`item-job-${i}`} value={it.job_id || ""}
            onChange={(e) => { const jid = e.target.value; setItem(i, { job_id: jid, job_name: jobs.find((j) => j.id === jid)?.customer_name || "", match_material_id: "" }); ensureMats(jid); }}
            className="h-10 w-full border border-zinc-200 rounded-lg px-2 bg-white text-sm">
            <option value="">Select a job…</option>
            {jobs.map((j) => <option key={j.id} value={j.id}>{j.customer_name || "Unnamed"} — {j.location || ""}</option>)}
          </select>

          {suggestion && it.match_material_id !== suggestion.id && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-2 flex items-center justify-between gap-2" data-testid={`item-match-suggest-${i}`}>
              <span className="text-xs text-green-800"><b>Match found:</b> {suggestion.name}</span>
              <button onClick={() => setItem(i, { match_material_id: suggestion.id })} className="text-xs font-bold text-green-700 underline" data-testid={`item-match-btn-${i}`}>Match</button>
            </div>
          )}
          {mats.length > 0 && (
            <select data-testid={`item-match-${i}`} value={it.match_material_id || ""} onChange={(e) => setItem(i, { match_material_id: e.target.value })}
              className="h-10 w-full border border-zinc-200 rounded-lg px-2 bg-white text-sm">
              <option value="">Add as NEW material</option>
              {mats.map((m) => <option key={m.id} value={m.id}>Match → {m.name}</option>)}
            </select>
          )}

          <div className="grid grid-cols-2 gap-2">
            <select data-testid={`item-method-${i}`} value={it.billing_method || "actual_markup"} onChange={(e) => setItem(i, { billing_method: e.target.value })}
              className="h-10 w-full border border-zinc-200 rounded-lg px-2 bg-white text-sm">
              {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {(it.billing_method === "actual_markup" || it.billing_method === "retail_markup") && (
              <label className="block"><span className="text-[10px] uppercase font-bold text-zinc-400">Markup %</span>
                <Input type="number" value={it.markup_pct ?? 25} onChange={(e) => setItem(i, { markup_pct: Number(e.target.value) })} className="h-9 mt-0.5" data-testid={`item-markup-${i}`} /></label>
            )}
            {it.billing_method === "retail_markup" && (
              <label className="block"><span className="text-[10px] uppercase font-bold text-zinc-400">Retail price</span>
                <Input type="number" value={it.retail_price ?? 0} onChange={(e) => setItem(i, { retail_price: Number(e.target.value) })} className="h-9 mt-0.5" data-testid={`item-retail-${i}`} /></label>
            )}
            {it.billing_method === "manual" && (
              <label className="block"><span className="text-[10px] uppercase font-bold text-zinc-400">Customer price</span>
                <Input type="number" value={it.manual_price ?? 0} onChange={(e) => setItem(i, { manual_price: Number(e.target.value) })} className="h-9 mt-0.5" data-testid={`item-manual-${i}`} /></label>
            )}
          </div>

          <label className="flex items-center gap-2 text-xs text-zinc-600" data-testid={`item-return-toggle-${i}`}>
            <input type="checkbox" checked={!!it.is_return} onChange={(e) => setItem(i, { is_return: e.target.checked })} />
            This is a return / refund
            {it.is_return && <Input type="number" placeholder="Refund $" value={it.refund_amount ?? 0} onChange={(e) => setItem(i, { refund_amount: Number(e.target.value) })} className="h-8 w-28 ml-auto" data-testid={`item-refund-${i}`} />}
          </label>
        </div>
      )}
    </div>
  );
};
